import React, { useState, useEffect, useMemo } from "react";
import {
  Calendar,
  Users,
  BookOpen,
  Award,
  Map,
  Bell,
  HelpCircle,
  QrCode,
  ClipboardList,
  AlertCircle,
  ShieldCheck,
  ChevronRight,
} from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useApp } from "../contexts/AppContext";
import TileGrid from "../components/TileGrid";
import { notificationsAPI } from "../services/api";
import "./Home.css";

const Home = () => {
  const { user } = useAuth();
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { unreadCount, setUnreadCount, currentEvent } = useApp();
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    fetchNotifications();
  }, []);

  useEffect(() => {
    if (currentEvent) {
      setAlert({
        type: "info",
        title: `Welcome to ${currentEvent.title}`,
        message: "Open Programme for session timings and venues",
      });
    }
  }, [currentEvent]);

  const fetchNotifications = async () => {
    try {
      const response = await notificationsAPI.getUnreadCount();
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
    }
  };

  const formatDateRange = (startStr, endStr) => {
    if (!startStr) return "";
    const start = new Date(startStr);
    const end = endStr ? new Date(endStr) : null;
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    if (!end || start.toDateString() === end.toDateString()) {
      return `${start.getDate()} ${monthNames[start.getMonth()]} ${start.getFullYear()}`;
    }
    if (start.getMonth() === end.getMonth()) {
      return `${start.getDate()}–${end.getDate()} ${monthNames[start.getMonth()]} ${start.getFullYear()}`;
    }
    return `${start.getDate()} ${monthNames[start.getMonth()]} – ${end.getDate()} ${monthNames[end.getMonth()]} ${start.getFullYear()}`;
  };

  const getDurationDays = (startStr, endStr) => {
    if (!startStr || !endStr) return "1 Day";
    const start = new Date(startStr).setHours(0, 0, 0, 0);
    const end = new Date(endStr).setHours(0, 0, 0, 0);
    const diff = Math.round(Math.abs(end - start) / (1000 * 60 * 60 * 24)) + 1;
    return `${diff} Day${diff > 1 ? "s" : ""}`;
  };

  const tiles = useMemo(() => {
    const prefix = eventId ? `/event/${eventId}` : "";
    return [
      {
        id: "my-qr",
        title: "My Pass",
        subtitle: "Conference entry & access",
        icon: <QrCode size={24} />,
        path: `${prefix}/my-qr`,
      },
      {
        id: "programme",
        title: "Programme",
        subtitle: "View full agenda",
        icon: <Calendar size={24} />,
        path: `${prefix}/programme`,
        color: "#0ea5e9",
      },
      {
        id: "speakers",
        title: "Speakers",
        subtitle: "Meet the experts",
        icon: <Users size={24} />,
        path: `${prefix}/speakers`,
        color: "#8b5cf6",
      },
      {
        id: "masterclasses",
        title: "Masterclasses",
        subtitle: "Limited seats | Registration Required",
        icon: <Award size={24} />,
        path: `${prefix}/masterclasses`,
        color: "#f59e0b",
      },
      {
        id: "my-schedule",
        title: "My Schedule",
        subtitle: "Registered & Saved sessions",
        icon: <BookOpen size={24} />,
        path: `${prefix}/my-schedule`,
        color: "#10b981",
      },
      {
        id: "venue-map",
        title: "Venue & Logistics",
        subtitle: "Maps & navigation",
        icon: <Map size={24} />,
        path: `${prefix}/venue`,
        color: "#6366f1",
      },
      {
        id: "notifications",
        title: "Announcements",
        subtitle: "Live Updates",
        icon: <Bell size={24} />,
        path: `${prefix}/notifications`,
        color: "#ec4899",
        badge: unreadCount > 0 ? unreadCount : null,
      },
      {
        id: "feedback",
        title: "Question and Answers",
        subtitle: "Session-wise audience questions",
        icon: <ClipboardList size={24} />,
        path: `https://www.csdms.reliancefoundation.org/user/lcncform/cmVsLTYy`,
        color: "#94a3b8",
      },
      {
        id: "survey",
        title: "Event Feedback",
        subtitle: "Share your experience",
        icon: <HelpCircle size={24} />,
        path: `https://www.csdms.reliancefoundation.org/user/lcncform/cmVsLTYx`,
        color: "#475569",
      },
      {
        id: "help",
        title: "Help & Support",
        subtitle: "(FAQs, Contact support, WhatsApp, Call, Email)",
        icon: <HelpCircle size={24} />,
        path: `${prefix}/help`,
        color: "#475569",
      },
    ];
  }, [eventId, unreadCount]);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 17) return "Good Afternoon";
    return "Good Evening";
  };

  const getFirstName = () => {
    // Check both 'name' and 'full_name' for compatibility
    const name = user?.full_name || user?.name || "Delegate";
    return name.split(" ")[0];
  };

  return (
    <div className="home-page">
      <div className="home-header">
        <div className="header-content">
          <div className="header-user-info">
            {user?.avatar_url && (
              <img
                src={user.avatar_url}
                alt={user.full_name}
                className="header-user-avatar"
                style={{
                  width: "60px",
                  height: "60px",
                  borderRadius: "50%",
                  objectFit: "cover",
                  border: "2px solid rgba(255, 255, 255, 0.3)",
                }}
              />
            )}
            <div className="header-greeting">
              <span className="greeting-text">{getGreeting()},</span>
              <h1 className="user-name">{getFirstName()}</h1>
            </div>
          </div>
          <div className="header-logo">
            <img
              src="/logo.png"
              alt="RF Logo"
              className="logo-mark"
              style={{
                background: "white",
                padding: "2px",
                objectFit: "contain",
              }}
            />
          </div>
        </div>

        <div className="header-wave">
          <svg viewBox="0 0 1440 60" preserveAspectRatio="none">
            <path d="M0,32L80,37.3C160,43,320,53,480,53.3C640,53,800,43,960,37.3C1120,32,1280,32,1360,32L1440,32L1440,60L1360,60C1280,60,1120,60,960,60C800,60,640,60,480,60C320,60,160,60,80,60L0,60Z" />
          </svg>
        </div>
      </div>

      {user?.is_admin && (
        <div className="admin-callout" onClick={() => navigate("/admin")}>
          <div className="admin-callout-content">
            <h3>Admin Control Center</h3>
            <p>Manage events, users, and session registrations.</p>
          </div>
          <div className="admin-badge-icon">
            <ShieldCheck size={28} />
          </div>
        </div>
      )}

      {alert && (
        <div className="home-alert-container">
          <div className={`alert-banner ${alert.type}`}>
            <AlertCircle size={24} className="alert-icon" />
            <div className="alert-content">
              <h4>{alert.title}</h4>
              <p>{alert.message}</p>
            </div>
            <button
              className="alert-dismiss"
              onClick={() => setAlert(null)}
              aria-label="Dismiss alert"
            >
              ×
            </button>
          </div>
        </div>
      )}

      <TileGrid tiles={tiles} />

      {/* <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-value">9+ Sessions</div>
          <div className="stat-label">Panels • Keynotes</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">3 Speakers</div>
          <div className="stat-label">Global & Indian experts</div>
        </div>
      </div> */}

    </div>
  );
};

export default Home;
